import React from "react";

import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { ButtonStandard, Icon, Text } from "@performyard/py-components";

type BulkEditBarProps = {
	isOpen: boolean;
	pendingCount: number;
	atCap: boolean;
	isSaving: boolean;
	onCancel: () => void;
	onSave: () => void;
};

// Goals-style action bar for a bulk level-edit session (PY-14092): sits flush on top of the table
// (chrome and slide-down mirror GoalsListBulkActionsBar) and owns the change count and the
// Cancel/Save actions. Always mounted so the max-height transition can animate open; while closed
// it is aria-hidden and its buttons are disabled, so nothing visually hidden stays reachable.
export const BulkEditBar = ({
	isOpen,
	pendingCount,
	atCap,
	isSaving,
	onCancel,
	onSave,
}: BulkEditBarProps): React.ReactElement => (
	<div
		className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? "max-h-14" : "max-h-0"}`}
		aria-hidden={!isOpen}
	>
		<div
			data-testid="bulk-edit-bar"
			data-state={isOpen ? "open" : "closed"}
			className="w-[100%-2px] rounded-t border border-b-0 border-solid border-neutral-2 bg-neutral-1"
		>
			<div className="flex h-12 items-center justify-between px-4">
				<div />
				<div className="flex flex-1 items-center justify-center gap-2">
					{pendingCount > 0 && (
						<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
							{`${pendingCount} Change${pendingCount === 1 ? "" : "s"}`}
						</Text>
					)}
					{atCap && (
						<>
							<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
								•
							</Text>
							<span data-testid="bulk-edit-cap-warning" className="flex items-center gap-1">
								<Icon icon={faCircleExclamation} size={16} color="red-5" />
								<Text size="paragraph-3" weight="regular" color="red-5">
									{`Save these ${pendingCount} changes to continue`}
								</Text>
							</span>
						</>
					)}
				</div>
				<div className="flex items-center gap-2">
					<ButtonStandard
						color="neutral"
						variant="outline"
						onClick={onCancel}
						disabled={!isOpen || isSaving}
						data-testid="bulk-edit-cancel"
					>
						Cancel
					</ButtonStandard>
					<ButtonStandard
						color="primary"
						onClick={onSave}
						disabled={!isOpen || pendingCount === 0 || isSaving}
						data-testid="bulk-edit-save"
					>
						Save Changes
					</ButtonStandard>
				</div>
			</div>
		</div>
	</div>
);
